import { useAlert } from "components/UI_components/Alert";
import React, { useState, useEffect } from "react";
import { FiArrowLeft } from "react-icons/fi";
import {
  MdAdminPanelSettings,
  MdTask,
  MdGroup,
  MdAssessment,
  MdInventory,
  MdAttachMoney,
  MdPeople,
} from "react-icons/md";
import { BASE_URL } from "utils/constant";

const Toggle = ({ enabled, onClick, disabled }) => (
  <div
    className={`${
      enabled ? "bg-[#3B0069]" : "bg-gray-300"
    } w-12 h-6 rounded-full relative transition-colors ${
      disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
    }`}
    onClick={disabled ? undefined : onClick}
  >
    <div
      className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-300 ${
        enabled ? "right-1" : "left-1"
      }`}
    />
  </div>
);

const PermissionCard = ({
  title,
  description,
  icon: Icon,
  enabled,
  onToggle,
  children,
  disabled,
}) => (
  <div
    className={`border border-gray-200 rounded-xl p-4 mb-4 transition-colors ${
      disabled
        ? "opacity-50 bg-gray-50"
        : "hover:border-purple-300 bg-white"
    }`}
  >
    <div className="flex items-start justify-between mb-2">
      <div className="flex items-start gap-3 flex-1">
        {Icon && (
          <Icon className="text-purple-600 w-5 h-5 mt-0.5 flex-shrink-0" />
        )}
        <div>
          <h3 className="font-semibold text-base text-gray-800">{title}</h3>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </div>
      <Toggle enabled={enabled} onClick={onToggle} disabled={disabled} />
    </div>
    {enabled && children && (
      <div className="mt-4 ml-8 space-y-3">{children}</div>
    )}
  </div>
);

const SubPermission = ({ label, checked, onChange, disabled }) => (
  <label
    className={`flex items-center gap-2 group ${
      disabled ? "cursor-not-allowed" : "cursor-pointer"
    }`}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
    />
    <span
      className={`text-sm text-gray-700 ${
        !disabled && "group-hover:text-gray-900"
      }`}
    >
      {label}
    </span>
  </label>
);

export default function ProjectAccess({ project, onBack, member }) {
  const {addAlert} = useAlert();
  // Main permissions
  const [siteAdmin, setSiteAdmin] = useState(member.is_admin || false);
  const [taskAuthority, setTaskAuthority] = useState(
    member.create_taskgroup || false
  );
  const [memberControl, setMemberControl] = useState(
    member.member_control || false
  );
  const [reportGeneration, setReportGeneration] = useState(
    member.report_generation || false
  );

  // Material permissions
  const [materialAccess, setMaterialAccess] = useState(
    member.edit_material_management || member.view_material_management || false
  );
  const [materialView, setMaterialView] = useState(
    member.view_material_management || false
  );
  const [materialEdit, setMaterialEdit] = useState(
    member.edit_material_management || false
  );

  // Finance permissions
  const [financeAccess, setFinanceAccess] = useState(
    member.edit_finance || member.view_finance || false
  );
  const [financeView, setFinanceView] = useState(member.view_finance || false);
  const [financeEdit, setFinanceEdit] = useState(member.edit_finance || false);

  // Worker permissions
  const [workerAccess, setWorkerAccess] = useState(
    member.edit_worker_management || member.view_worker_management || false
  );
  const [workerView, setWorkerView] = useState(
    member.view_worker_management || false
  );
  const [workerEdit, setWorkerEdit] = useState(
    member.edit_worker_management || false
  );

  // When Site Admin changes, update all other permissions
  useEffect(() => {
    if (siteAdmin) {
      setTaskAuthority(true);
      setMemberControl(true);
      setReportGeneration(true);
      setMaterialAccess(true);
      setMaterialView(true);
      setMaterialEdit(true);
      setFinanceAccess(true);
      setFinanceView(true);
      setFinanceEdit(true);
      setWorkerAccess(true);
      setWorkerView(true);
      setWorkerEdit(true);
    }
  }, [siteAdmin]);

  const handleSubmit = async () => {
    const payload = {
      create_taskgroup: taskAuthority,
      is_admin: siteAdmin,
      edit_all: siteAdmin || (workerEdit && materialEdit && financeEdit),
      view_all: siteAdmin || (workerView && materialView && financeView),
      edit_finance: financeEdit,
      view_finance: financeView,
      edit_material_management: materialEdit,
      view_material_management: materialView,
      edit_worker_management: workerEdit,
      view_worker_management: workerView,
      member_control: memberControl,
      report_generation: reportGeneration,
      organization_member: member.organization_member_id,
      sites: project.id,
    };

    try {
      const response = await fetch(
        `${BASE_URL}/team/update-member/${member.member_id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify(payload),
        }
      );
      if (response.ok) {
        addAlert("Permissions updated successfully", "success");
        onBack();
      } else {
        addAlert("Failed to update permissions", "error");
      }
    } catch (error) {
      console.error("Error updating permissions:", error);
      addAlert("An error occurred while updating permissions", "error");
    }
  };

  if (!project) return <div>No project selected.</div>;

  return (
    <div className="bg-white rounded-t-2xl min-h-screen">
      {/* Header */}
      <div className="flex items-center px-4 py-4 border-b border-gray-100">
        <button
          className="mr-3 text-gray-700 hover:text-black text-xl focus:outline-none"
          onClick={onBack}
          aria-label="Back"
        >
          <FiArrowLeft />
        </button>
        <div className="flex flex-col">
          <span className="font-semibold text-lg leading-tight">
            {project.name}
          </span>
          <span className="text-gray-500 text-sm leading-tight">
            {project.address ||
              project.site_address ||
              project.location ||
              "No address"}
          </span>
        </div>
      </div>

      {/* Permissions */}
      <div className="p-6 space-y-4">
        {/* Site Admin */}
        <PermissionCard
          title="Site Admin"
          description="Full administrative access to all site features and settings"
          icon={MdAdminPanelSettings}
          enabled={siteAdmin}
          onToggle={() => setSiteAdmin(!siteAdmin)}
        />

        {/* Task Authority */}
        <PermissionCard
          title="Task Authority"
          description="Create, assign, and manage tasks across the project"
          icon={MdTask}
          enabled={taskAuthority}
          onToggle={() => setTaskAuthority(!taskAuthority)}
          disabled={siteAdmin}
        />

        {/* Member Control */}
        <PermissionCard
          title="Member Control"
          description="Add, remove, and manage team member access and roles"
          icon={MdGroup}
          enabled={memberControl}
          onToggle={() => setMemberControl(!memberControl)}
          disabled={siteAdmin}
        />

        {/* Report Generation */}
        <PermissionCard
          title="Report Generation"
          description="Generate and export project reports and analytics"
          icon={MdAssessment}
          enabled={reportGeneration}
          onToggle={() => setReportGeneration(!reportGeneration)}
          disabled={siteAdmin}
        />

        {/* Material */}
        <PermissionCard
          title="Material"
          description="Access to inventory, stock management, and material tracking"
          icon={MdInventory}
          enabled={materialAccess}
          onToggle={() => {
            if (materialAccess) {
              setMaterialView(false);
              setMaterialEdit(false);
            } else {
              setMaterialView(true);
              setMaterialEdit(true);
            }
            setMaterialAccess(!materialAccess);
          }}
          disabled={siteAdmin}
        >
          <SubPermission
            label="View material data and inventory"
            checked={materialView}
            onChange={() => setMaterialView(!materialView)}
            disabled={siteAdmin}
          />
          <SubPermission
            label="Edit, add, and update material records"
            checked={materialEdit}
            onChange={() => setMaterialEdit(!materialEdit)}
            disabled={siteAdmin}
          />
        </PermissionCard>

        {/* Finance */}
        <PermissionCard
          title="Finance"
          description="Access to transactions, payments, and financial records"
          icon={MdAttachMoney}
          enabled={financeAccess}
          onToggle={() => {
            if (financeAccess) {
              setFinanceView(false);
              setFinanceEdit(false);
            } else {
              setFinanceView(true);
              setFinanceEdit(true);
            }
            setFinanceAccess(!financeAccess);
          }}
          disabled={siteAdmin}
        >
          <SubPermission
            label="View financial data and transactions"
            checked={financeView}
            onChange={() => setFinanceView(!financeView)}
            disabled={siteAdmin}
          />
          <SubPermission
            label="Edit, add, and process transactions"
            checked={financeEdit}
            onChange={() => setFinanceEdit(!financeEdit)}
            disabled={siteAdmin}
          />
        </PermissionCard>

        {/* Workers */}
        <PermissionCard
          title="Workers"
          description="Access to worker details, attendance, and payroll"
          icon={MdPeople}
          enabled={workerAccess}
          onToggle={() => {
            if (workerAccess) {
              setWorkerView(false);
              setWorkerEdit(false);
            } else {
              setWorkerView(true);
              setWorkerEdit(true);
            }
            setWorkerAccess(!workerAccess);
          }}
          disabled={siteAdmin}
        >
          <SubPermission
            label="View worker details and attendance"
            checked={workerView}
            onChange={() => setWorkerView(!workerView)}
            disabled={siteAdmin}
          />
          <SubPermission
            label="Edit, add, and manage worker records"
            checked={workerEdit}
            onChange={() => setWorkerEdit(!workerEdit)}
            disabled={siteAdmin}
          />
        </PermissionCard>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 rounded-xl font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="bg-[#3B0069] text-white px-6 py-2 rounded-xl font-semibold shadow hover:bg-purple-800 transition"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}